import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  SearchBar,
  Pagination,
  Button,
  ConfirmDialog,
  type TableColumn,
  type SortConfig,
} from '../../components/aip';
import { getAdminUsers, deleteAdminUser, deleteAdminUsersBatch, type AdminUser } from '../../services/manageNewApi';

/**
 * ユーザ一覧ページ
 */
export const AdminUserListPage: React.FC = () => {
  // データ状態
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 検索・フィルター状態
  const [searchText, setSearchText] = useState('');

  // ページネーション状態
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ソート状態
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'admin_id', direction: 'asc' });

  // 選択状態
  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);

  // 削除ダイアログ状態
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 一括削除ダイアログ状態
  const [showBatchDeleteDialog, setShowBatchDeleteDialog] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);

  // データ取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAdminUsers();
        setUsers(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // フィルター処理
  const filteredData = useMemo(() => {
    let result = [...users];

    // 検索フィルター
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      result = result.filter(
        (u) =>
          u.admin_code?.toLowerCase().includes(lowerSearch) ||
          u.admin_number?.toLowerCase().includes(lowerSearch) ||
          u.admin_name?.toLowerCase().includes(lowerSearch) ||
          u.admin_email?.toLowerCase().includes(lowerSearch) ||
          u.status?.toLowerCase().includes(lowerSearch)
      );
    }

    // ソート処理
    if (sortConfig.key && sortConfig.direction) {
      result.sort((a, b) => {
        const aValue = (a as any)[sortConfig.key];
        const bValue = (b as any)[sortConfig.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        let comparison = 0;
        if (typeof aValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else {
          comparison = aValue - bValue;
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [users, searchText, sortConfig]);

  // ページネーション処理
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // ページ変更時に先頭ページに戻す
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, pageSize]);

  // 削除処理
  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await deleteAdminUser(deleteTarget.admin_id);
      setUsers((prev) => prev.filter((u) => u.admin_id !== deleteTarget.admin_id));
      setDeleteTarget(null);
      setSelectedKeys((prev) => prev.filter((k) => k !== deleteTarget.admin_id));
    } catch (err) {
      setError(err instanceof Error ? err.message : '削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  };

  // 一括削除処理
  const handleBatchDelete = async () => {
    if (selectedKeys.length === 0) return;

    try {
      setBatchDeleting(true);
      const ids = selectedKeys.map((k) => Number(k));
      await deleteAdminUsersBatch(ids);
      setUsers((prev) => prev.filter((u) => !selectedKeys.includes(u.admin_id)));
      setSelectedKeys([]);
      setShowBatchDeleteDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : '一括削除に失敗しました');
    } finally {
      setBatchDeleting(false);
    }
  };

  // 権限バッジの表示
  const renderRoleBadge = (isAdmin: number) => {
    const isAdminUser = isAdmin === 1;
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '0.125rem 0.5rem',
          borderRadius: '0.25rem',
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: isAdminUser ? 'var(--color-blue100)' : 'var(--color-gray100)',
          color: isAdminUser ? 'var(--color-blue700)' : 'var(--color-gray700)',
        }}
      >
        {isAdminUser ? '管理者' : '一般'}
      </span>
    );
  };

  // テーブルカラム定義
  const columns: TableColumn<AdminUser>[] = [
    {
      key: 'admin_name',
      label: 'ユーザ名',
      sortable: true,
      sortType: 'text',
      render: (value, row) => (
        <Link className="aip-link" to={`/meeting/manage-new/admin-users/${row.admin_id}`}>
          {value || '-'}
        </Link>
      ),
    },
    {
      key: 'admin_number',
      label: '社員番号',
      sortable: true,
      sortType: 'text',
      render: (value) => value || '-',
    },
    {
      key: 'admin_email',
      label: 'メールアドレス（ログインID）',
      sortable: true,
      sortType: 'text',
      render: (value) => value || '-',
    },
    {
      key: 'is_admin',
      label: '権限',
      sortable: true,
      sortType: 'number',
      render: (value) => renderRoleBadge(value ?? 0),
    },
  ];

  // ヘッダーコンテンツ
  const headerContent = (
    <div className="aip-table__header-area">
      <div className="aip-table__header-buttons">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="テーブル内検索"
          icon="manage_search"
        />
      </div>
      <div className="aip-table__header-buttons">
        <Button
          as="link"
          to="/meeting/manage-new/admin-users/new"
          buttonType="solid-fill"
          size="sm"
          iconPosition="start"
        >
          <span className="material-symbols-outlined">add</span>
          作成
        </Button>
      </div>
    </div>
  );

  // 選択時アクション
  const selectionActions = (
    <Button
      buttonType="icon-only"
      size="sm"
      state="alert"
      onClick={() => setShowBatchDeleteDialog(true)}
      aria-label="選択項目を削除"
    >
      <span className="material-symbols-outlined">delete</span>
    </Button>
  );

  // フッターコンテンツ
  const footerContent = (
    <Pagination
      currentPage={currentPage}
      totalPages={totalPages || 1}
      pageSize={pageSize}
      onPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
    />
  );

  // 操作カラム
  const renderActions = (row: AdminUser) => (
    <>
      <Button
        as="link"
        to={`/meeting/manage-new/admin-users/${row.admin_id}/edit`}
        buttonType="icon-only"
        size="sm"
        aria-label="編集"
      >
        <span className="material-symbols-outlined">edit</span>
      </Button>
      <Button
        buttonType="icon-only"
        size="sm"
        state="alert"
        onClick={() => setDeleteTarget(row)}
        aria-label="削除"
      >
        <span className="material-symbols-outlined">delete</span>
      </Button>
    </>
  );

  // ローディング表示
  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        読み込み中...
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <div
        style={{
          padding: '1rem',
          backgroundColor: 'var(--color-red50)',
          color: 'var(--color-alert)',
          borderRadius: '0.5rem',
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <>
      <Table
        data={paginatedData}
        columns={columns}
        getRowKey={(row) => row.admin_id}
        size="dense"
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        selectionActions={selectionActions}
        sortConfig={sortConfig}
        onSortChange={setSortConfig}
        renderActions={renderActions}
        emptyMessage="ユーザがいません"
        emptyDescription="新しいユーザを作成してください"
        emptyIcon="person_off"
        headerContent={headerContent}
        footerContent={footerContent}
      />

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="ユーザの削除"
        message={
          <>
            <strong>{deleteTarget?.admin_name || deleteTarget?.admin_code}</strong> を削除しますか？
            <br />
            この操作は元に戻せません。
          </>
        }
        confirmLabel="削除"
        cancelLabel="キャンセル"
        confirmType="alert"
        loading={deleting}
      />

      {/* 一括削除確認ダイアログ */}
      <ConfirmDialog
        open={showBatchDeleteDialog}
        onClose={() => setShowBatchDeleteDialog(false)}
        onConfirm={handleBatchDelete}
        title="ユーザの一括削除"
        message={
          <>
            選択した <strong>{selectedKeys.length}件</strong> のユーザを削除しますか？
            <br />
            この操作は元に戻せません。
          </>
        }
        confirmLabel="削除"
        cancelLabel="キャンセル"
        confirmType="alert"
        loading={batchDeleting}
      />
    </>
  );
};

export default AdminUserListPage;

